import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Voicemail } from '@/types/voicemails'

interface VoicemailsResponse {
  voicemails: Voicemail[]
}

const voicemailsKey = (companyId: number | null) => ['voicemails', companyId]

export function useVoicemails(companyId: number | null) {
  return useQuery({
    queryKey: voicemailsKey(companyId),
    queryFn: async () => {
      if (!companyId) return [] as Voicemail[]
      const { data } = await api.get<VoicemailsResponse>(
        `/api/companies/${companyId}/voicemails/`,
      )
      return data.voicemails || []
    },
    enabled: !!companyId,
  })
}

export function useDeleteVoicemail(companyId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      if (!companyId) return
      await api.delete(`/api/companies/${companyId}/voicemails/${id}/`)
    },
    onSuccess: (_, id) => {
      queryClient.setQueryData<Voicemail[]>(
        voicemailsKey(companyId),
        (prev) => prev?.filter((v) => v.id !== id),
      )
    },
  })
}

export function useMarkVoicemailRead(companyId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      if (!companyId) return
      // Backend (views.py:1665) attend `{ action: 'mark_read' }`,
      // pas `{ is_read: true }` — sinon réponse 400.
      await api.patch(`/api/companies/${companyId}/voicemails/${id}/`, {
        action: 'mark_read',
      })
    },
    onSuccess: (_, id) => {
      queryClient.setQueryData<Voicemail[]>(
        voicemailsKey(companyId),
        (prev) => prev?.map((v) => (v.id === id ? { ...v, is_read: true } : v)),
      )
      // Le badge sidebar tire `unread_voicemail_count` de la 1ère page
      // de call-logs → invalider pour rafraîchir.
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
    },
  })
}

/**
 * Marque en lu tous les voicemails non lus en parallèle. Source de vérité :
 * le cache `['call-logs']` (premier page) où chaque entrée a
 * `type='voicemail'` et `is_read`. Idempotent : si tout est déjà lu, no-op.
 *
 * Utilisé à l'ouverture du PhoneTab (autre que le mode dialpad pur) pour
 * vider automatiquement le badge sidebar — le transcript étant visible
 * dans la liste, on considère que l'user a "lu" le message.
 */
export function useMarkAllVoicemailsRead(companyId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (unreadIds: number[]) => {
      if (!companyId || unreadIds.length === 0) return { count: 0 }
      // allSettled : on n'abandonne pas tout si un PATCH échoue
      const results = await Promise.allSettled(
        unreadIds.map((id) =>
          api.patch(`/api/companies/${companyId}/voicemails/${id}/`, {
            action: 'mark_read',
          }),
        ),
      )
      const count = results.filter((r) => r.status === 'fulfilled').length
      return { count }
    },
    onSuccess: ({ count }) => {
      if (count === 0) return
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      queryClient.invalidateQueries({ queryKey: voicemailsKey(companyId) })
    },
  })
}
